import consumer from "./consumer";

document.addEventListener("turbolinks:load", () => {
  const projectContainer = document.querySelector('[data-project-id]');
  if (!projectContainer) return;

  const projectId = projectContainer.dataset.projectId;
  const accountId = projectContainer.dataset.accountId;
  const currentUserId = projectContainer.dataset.currentUserId;

  if (!projectId || !accountId) {
    console.log("Project ID or Account ID not found, subscription not created.");
    return;
  }

  const subscription = consumer.subscriptions.create(
    { 
      channel: "ProjectUpdatesChannel", 
      project_id: projectId,
      account_id: accountId
    },
    {
      connected() {
        console.log("Connected to ProjectUpdatesChannel");
      },

      disconnected() {
        console.log("Disconnected from ProjectUpdatesChannel");
      },

      received(data) {
        // Only process updates intended for the current user
        if (data.user_id && data.user_id.toString() !== currentUserId) {
          return;
        }

        console.log("ProjectUpdatesChannel received:", data);

        switch (data.type) {
          case 'task_completion':
            this.handleTaskCompletion(data);
            break;
          case 'block_update':
            this.handleBlockUpdate(data);
            break;
          case 'task_visibility_change':
            this.handleTaskVisibilityChange(data);
            break;
          case 'propagation_complete':
            this.handlePropagationComplete(data);
            break;
          default:
            console.log("Unknown update type:", data.type);
        }
      },

      handleTaskCompletion(data) {
        const taskElement = document.querySelector(`[data-task-id="${data.task_id}"]`);
        if (!taskElement) return;

        // Update task completion status
        if (data.completed) {
          taskElement.classList.add('complete');
          taskElement.classList.remove('incomplete');
        } else {
          taskElement.classList.remove('complete');
          taskElement.classList.add('incomplete');
        }

        // Update checkbox state
        const checkboxContainer = taskElement.querySelector('.step-checkbox');
        if (checkboxContainer) {
          this.updateTaskCheckbox(checkboxContainer, data.completed);
        }

        // Update task meta information
        this.updateTaskMeta(taskElement, data);

        // Show completion notification
        this.showCompletionNotification(data);

        // Update task counts if available
        this.updateTaskCounts(data);
      },

      handleBlockUpdate(data) {
        const taskElement = document.querySelector(`[data-task-id="${data.task_id}"]`);
        if (!taskElement) return;

        const blockElement = taskElement.querySelector(`[data-block-id="${data.block_id}"]`);
        if (!blockElement) return;

        // Refresh the block content
        this.refreshBlockContent(blockElement, data);
      },

      handleTaskVisibilityChange(data) {
        const taskElement = document.querySelector(`[data-task-id="${data.task_id}"]`);
        if (!taskElement) return;

        if (data.hidden || !data.can_see) {
          // Hide the task
          taskElement.classList.add('task-hidden');
          taskElement.style.display = 'none';
        } else {
          // Show the task
          taskElement.classList.remove('task-hidden');
          taskElement.style.display = '';
        }

        // Update task counts
        this.updateTaskCounts(data);
      },

      updateTaskCheckbox(container, completed) {
        const completeLink = container.querySelector('.complete-link');
        const uncompleteLink = container.querySelector('.uncomplete-link');
        const checkboxWrap = container.querySelector('.checkbox-wrap');

        if (completed) {
          if (completeLink) completeLink.classList.add('hidden');
          if (uncompleteLink) uncompleteLink.classList.remove('hidden');
          if (checkboxWrap) checkboxWrap.classList.add('hidden');
        } else {
          if (completeLink) completeLink.classList.remove('hidden');
          if (uncompleteLink) uncompleteLink.classList.add('hidden');
          if (checkboxWrap) checkboxWrap.classList.remove('hidden');
        }
      },

      updateTaskMeta(taskElement, data) {
        const metaElement = taskElement.querySelector('.step-meta');
        if (!metaElement) return;

        // Update completion time if available
        if (data.completed_at) {
          const completionTimeElement = metaElement.querySelector('.completion-time');
          if (completionTimeElement) {
            completionTimeElement.textContent = new Date(data.completed_at).toLocaleString();
          }
        }

        // Update completed by information
        if (data.completed_by) {
          const completedByElement = metaElement.querySelector('.completed-by');
          if (completedByElement) {
            // You might want to fetch the user name here or include it in the data
            completedByElement.textContent = `Completed by User #${data.completed_by}`;
          }
        }
      },

      refreshBlockContent(blockElement, data) {
        // For now, we'll just add a visual indicator that the block was updated
        // In a full implementation, you might want to fetch the updated content via AJAX
        blockElement.classList.add('block-updated');
        
        // Remove the indicator after a few seconds
        setTimeout(() => {
          blockElement.classList.remove('block-updated');
        }, 3000);

        // If you want to actually refresh the content, you could make an AJAX call here
        // this.fetchUpdatedBlockContent(blockElement, data);
      },

      showCompletionNotification(data) {
        // Create a temporary notification
        const notification = document.createElement('div');
        notification.className = 'real-time-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <span class="notification-icon">${data.completed ? '✅' : '🔄'}</span>
            <span class="notification-text">
              Task "${data.task_title || 'Unknown'}" ${data.completed ? 'completed' : 'uncompleted'}
            </span>
          </div>
        `;

        // Add to the page
        document.body.appendChild(notification);

        // Remove after 3 seconds
        setTimeout(() => {
          notification.remove();
        }, 3000);
      },

      updateTaskCounts(data) {
        // Update task list counts if available
        const taskListElement = document.querySelector(`[data-task-list-id="${data.task_list_id}"]`);
        if (!taskListElement) return;

        // Update total tasks count
        const totalTasksElement = taskListElement.querySelector('.total-tasks-count');
        if (totalTasksElement && data.visible_tasks) {
          totalTasksElement.textContent = data.visible_tasks.length;
        }

        // Update completed tasks count
        const completedTasksElement = taskListElement.querySelector('.completed-tasks-count');
        if (completedTasksElement && data.visible_tasks) {
          const completedCount = data.visible_tasks.filter(task => task.completed).length;
          completedTasksElement.textContent = completedCount;
        }

        // Update incomplete tasks count
        const incompleteTasksElement = taskListElement.querySelector('.incomplete-tasks-count');
        if (incompleteTasksElement && data.visible_tasks) {
          const incompleteCount = data.visible_tasks.filter(task => !task.completed).length;
          incompleteTasksElement.textContent = incompleteCount;
        }
      },

      fetchUpdatedBlockContent(blockElement, data) {
        // This method could be used to fetch updated block content via AJAX
        // For now, it's a placeholder for future implementation
        console.log("Would fetch updated content for block:", data.block_id);
      },

      handlePropagationComplete(data) {
        console.log("Propagation complete received for project:", data.project_id);
        
        
        this.showPropagationNotification(data);
        
        
        const currentPath = window.location.pathname;
        const taskListMatch = currentPath.match(/\/task_lists\/(\d+)/);
        const isTaskListPage = taskListMatch !== null;
        
        
        if (isTaskListPage && data.task_list_id) {
          const currentTaskListId = parseInt(taskListMatch[1]);
          if (currentTaskListId === data.task_list_id) {
            
            if (typeof Turbolinks !== 'undefined') {
              Turbolinks.visit(window.location.href);
            } else {
              window.location.reload();
            }
            return;
          }
        }
        
        
        const projectTasksMatch = currentPath.match(/\/projects\/(\d+)\/tasks/);
        if (projectTasksMatch && parseInt(projectTasksMatch[1]) === data.project_id) {
          if (typeof Turbolinks !== 'undefined') {
            Turbolinks.visit(window.location.href);
          } else {
            window.location.reload();
          }
        }
      },

      showPropagationNotification(data) {
        
        const notification = document.createElement('div');
        notification.className = 'real-time-notification propagation-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <span class="notification-icon">🔄</span>
            <span class="notification-text">
              Process has been updated. Refreshing...
            </span>
          </div>
        `;

        
        document.body.appendChild(notification);

        
        setTimeout(() => {
          notification.remove();
        }, 3000);
      }
    }
  );
});
